<script lang="ts">
  import Button from './ui/button.svelte';

  import { t } from '$lib/i18n';
  import { page } from '$app/stores';
  import { buildPath } from '$lib/utils/paths';
  import Seo from './seo.svelte';
  import type { TranslationKey } from '$lib/i18n/types';
  import ProductGrid from './product-grid.svelte';
  import { createQueries } from '@tanstack/svelte-query';
  import { shopify } from '$lib/shopify';
  import { lang, regionLanguage } from '$lib/lang';
  import { optimalUSCountry, parseCountryAsCode } from '$lib/country';
  import type { LanguageCode } from '$gql/storefront/schemas';
  import { cn } from '$lib/utils';
  import type { ProductCardFragment } from '$gql/storefront/operations';
  import { currentLocation } from '$lib/region';

  const STATUS_CODE_TO_I18N_TITLE_KEY: { [key: number]: TranslationKey } = {
    404: 'error-page.404.title',
    500: 'error-page.500.title',
    410: 'error-page.410.title'
  };

  const title = $t(
    STATUS_CODE_TO_I18N_TITLE_KEY[$page.status] || STATUS_CODE_TO_I18N_TITLE_KEY[500]
  );

  const STATUS_CODE_TO_I18N_DESCRIPTION_KEY: { [key: number]: TranslationKey } = {
    500: 'error-page.500.description'
  };

  const descriptionI18nKey = STATUS_CODE_TO_I18N_DESCRIPTION_KEY[$page.status];
  const description = descriptionI18nKey && $t(descriptionI18nKey);

  let screenSize = 0;
  let containerWidth = 0;

  const handles = ($page.data?.configuration?.content?.products_404 || [])
    .map((product) => (typeof product !== 'string' ? product.slug : null))
    .filter(Boolean) as string[];

  const queries = createQueries(
    handles.map((handle) => ({
      staleTime: 5 * 60 * 1000,
      queryKey: ['productCard', handle],
      queryFn: async () => {
        return await $shopify.getProductCard({
          handle,
          lang: $lang.toUpperCase() as LanguageCode,
          country: parseCountryAsCode($optimalUSCountry)
        });
      },
      onError: (err: Error) => {
        console.error(`Error loading product card for handle: ${handle}`, err);
      }
    }))
  );

  $: products = [...($queries || []).map((query) => query?.data?.product)].filter(
    (p) => p != null
  ) as ProductCardFragment[];

  $: href = $buildPath({ path: '/', lang: $regionLanguage, region: $currentLocation });
</script>

<svelte:window bind:innerWidth={screenSize} />
<Seo title={`${title}${$t('error-page.seo.title-prefix')}`} />
<div
  class={cn(
    $page.status != 410 && 'min-h-[50vh] overflow-hidden grid place-items-center text-center'
  )}
>
  <div
    class={cn(
      'flex justify-center items-center w-[100vw] h-[80vh] max-h-[400px] relative',
      $page.status == 410 ? 'min-h-[50vh] text-center' : 'max-w-[800px] '
    )}
  >
    <div>
      <h1 class="mb-6 text-7xl font-semibold">{title}</h1>

      {#if description}
        <p class="mb-4">{description}</p>
      {/if}

      <Button name="go-home" as="a" size="md" variant="accent" {href}>
        {$t('404.cta')}
      </Button>
    </div>
  </div>

  {#if $page.status == 410 && products.length != 0}
    <section class="px-2 lg:px-8 !py-0">
      <div class="container mx-auto lg:pt-0 pt-[88px] mb-2 lg:mb-0 relative">
        <h2 class="px-2 lg:px-0 text-4xl font-semibold top-[88px] lg:absolute left-0 right-0">
          You may also like
        </h2>
      </div>
      <div class="lg:pt-[88px]">
        <div
          class="py-2 lg:py-8 lg:pt-0"
          style:--x-margin={`${(screenSize - containerWidth) / 2 || 16}px`}
        >
          <div aria-hidden="true" class="container mx-auto" bind:clientWidth={containerWidth} />

          <ProductGrid {products} loading={$queries.some((q) => q.isLoading)} />
        </div>
      </div>
    </section>
  {/if}
</div>
